import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import api from "../../services/api.js";
import AddBillableItemModal from "./BillableItemAddModal.js";

export default function BillableItemList({ role, cust_id, token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const formatRupiah = (num) => {
    const parsed = parseFloat(num);
    if (isNaN(parsed) || parsed === 0) return "-";
    return parsed.toLocaleString("id-ID");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchBillableItems = useCallback(
    async (pageNumber = 1) => {
      if (!cust_id || !token) return;

      setLoading(true);
      try {
        const res = await api.get(`/billable-item/${cust_id}`, {
          params: { page: pageNumber, limit: 10 },
          headers: { Authorization: token },
        });

        const result = res.data;
        setItems(result.data || []);
        setTotalPages(result.pagination?.totalPages || 1);
        setPage(pageNumber);
      } catch (err) {
        console.error("fetchBillableItems error:", err);
        Swal.fire(
          "Error",
          err.response?.data?.message || "Gagal mengambil data",
          "error"
        );
      } finally {
        setLoading(false);
      }
    },
    [cust_id, token]
  );

  useEffect(() => {
    fetchBillableItems(1);
  }, [fetchBillableItems, refreshTrigger]);

  return (
    <>
      {/* HEADER */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-5 bg-blue-600 rounded-full"></div>
          <h2 className="text-xl font-bold dark:text-white">Billable Items</h2>
        </div>
      </div>

      {/* create billable item */}
      <div className="flex flex-wrap gap-3 mb-4">
        {role === "billing" && (
          <button
            disabled={loading}
            onClick={() => setOpenModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 ml-auto rounded shadow transition-colors"
          >
            + Tambah
          </button>
        )}

        {/* MODAL */}
        <AddBillableItemModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          userid={cust_id}
          token={token}
          onSuccess={() => setRefreshTrigger((v) => v + 1)}
        />
      </div>

      {/* CONTENT */}
      {loading ? (
        <p className="text-gray-500 dark:text-gray-400">
          Loading billable items...
        </p>
      ) : !items.length ? (
        <div className="p-8 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-lg text-center">
          <p className="text-red-500 dark:text-red-300">
            No billable items found for this customer.
          </p>
        </div>
      ) : (
        <motion.div
          className="w-full overflow-x-auto"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
                <th className="p-3 border-b dark:border-gray-700 text-sm">
                  Date
                </th>
                <th className="p-3 border-b dark:border-gray-700 text-sm">
                  Description
                </th>
                <th className="p-3 border-b dark:border-gray-700 text-sm">
                  Type
                </th>
                <th className="p-3 border-b dark:border-gray-700 text-sm">
                  Amount
                </th>
                <th className="p-3 border-b dark:border-gray-700 text-sm">
                  Status
                </th>
              </tr>
            </thead>

            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                  <td className="p-3 border-b dark:border-gray-700 text-sm whitespace-nowrap">
                    {formatDate(item.created_at)}
                  </td>

                  <td className="p-3 border-b dark:border-gray-700">
                    <div className="text-sm font-medium dark:text-gray-200">
                      {item.description}
                    </div>
                    <div className="text-[10px] text-gray-400">
                      Ref: {item.reference_id || "-"}
                    </div>
                  </td>

                  <td className="p-3 border-b dark:border-gray-700 text-sm">
                    <span
                      className={`px-2 py-1 rounded text-[11px] font-semibold ${
                        item.direction === "IN"
                          ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                          : "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
                      }`}
                    >
                      {item.direction}
                    </span>
                  </td>

                  <td
                    className={`p-3 border-b dark:border-gray-700 text-sm font-bold ${
                      item.direction === "IN"
                        ? "text-green-600"
                        : "text-red-500"
                    }`}
                  >
                    {item.direction === "IN" ? "+" : "-"}{" "}
                    {formatRupiah(item.amount)}
                  </td>

                  <td className="p-3 border-b dark:border-gray-700 text-sm">
                    <span
                      className={`px-2 py-1 rounded text-[11px] ${
                        item.status === "PENDING"
                          ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300"
                          : item.status === "INVOICED"
                          ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                          : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      )}

      {/* PAGINATION */}
      <div className="flex justify-end gap-2 mt-4">
        <button
          disabled={page <= 1}
          onClick={() => fetchBillableItems(page - 1)}
          className="px-3 py-1 text-sm rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 disabled:opacity-50"
        >
          Prev
        </button>
        <span className="text-sm px-2 py-1 dark:text-gray-300">
          {page} / {totalPages}
        </span>
        <button
          disabled={page >= totalPages}
          onClick={() => fetchBillableItems(page + 1)}
          className="px-3 py-1 text-sm rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </>
  );
}
